import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT, Environment } from '@cfp/core';
import { BuildingSearchFiltersService } from '@cfp/shared/building/filters';
import { RoadmapPortfolioDetails } from '@cfp/shared/roadmap';
import { LabelGroupService } from '@cfp/shared/ui/label-group';
import { TranslateModule } from '@ngx-translate/core';
import {
  CustomerByPortfolioService,
  PortfoliosService,
  RoadmapPortfolioDetailsDisplayLabelsYearDTO
} from 'csr-asset-app';
// eslint-disable-next-line import/no-restricted-paths -- Roadmap is in domains, we should investigate how we can move it to features-shared]
import { RoadmapChart, RoadmapChartLabels, RoadmapModule } from '@cfp/domains/roadmap';
import { PortfolioBusinessCaseCard } from '@cfp/shared/portfolio/business-case';
import { PortfolioEnergyLabelRatings } from '@cfp/shared/portfolio/energy-label-ratings';
import { PortfolioEnergyLabelsDistributionCard } from '@cfp/shared/portfolio/energy-labels';
import { PortfolioLocationsMap } from '@cfp/shared/portfolio/locations-map';
import { PortfolioSummaryComponent } from '@cfp/shared/portfolio/summary';
import { PortfolioTopMeasuresCard } from '@cfp/shared/portfolio/top-measures-card';
import { combineLatest, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { mapReportCustomerNameAndBusinessKey } from '../../utils';
import { PortfolioReport } from '../report';

@Component({
  selector: 'app-portfolio-report-summary',
  styleUrls: ['portfolio-report-summary.scss'],
  templateUrl: 'portfolio-report-summary.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    PortfolioSummaryComponent,
    PortfolioBusinessCaseCard,
    PortfolioLocationsMap,
    PortfolioEnergyLabelsDistributionCard,
    PortfolioEnergyLabelRatings,
    TranslateModule,
    NgClass,
    RoadmapModule,
    PortfolioReport,
    RoadmapChart,
    RoadmapChartLabels,
    PortfolioTopMeasuresCard
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortfolioReportSummary {
  private readonly environment = inject<Environment>(ENVIRONMENT);
  private readonly portfoliosService = inject(PortfoliosService);
  private readonly customerByPortfolioService = inject(CustomerByPortfolioService);
  private readonly labelGroupService = inject(LabelGroupService);
  private readonly buildingSearchFiltersService = inject(BuildingSearchFiltersService);

  public readonly details = input<RoadmapPortfolioDetails | undefined>(undefined);
  public readonly hasRoadmap = input<boolean>(false);
  public readonly hasRoadmapLicence = input<boolean>(false);
  public readonly portfolioId = input<number | undefined>(undefined);
  public readonly totals = input<
    | {
        [key: string]: RoadmapPortfolioDetailsDisplayLabelsYearDTO;
      }
    | undefined
  >(undefined);
  public readonly customLogo = input<string | undefined>(undefined);

  protected readonly logo = this.environment.images.logoReport || this.environment.images.logoNavbar;
  protected readonly showExplanationPage = this.environment.components.portfolioReportShowExplanationPage === true;
  protected readonly customer = toSignal(
    toObservable(this.portfolioId).pipe(
      switchMap((portfolioId) => {
        if (!portfolioId) {
          return of(undefined);
        }
        return this.customerByPortfolioService.v2CustomerByPortfolioIdGet(portfolioId);
      })
    )
  );
  private readonly reportCustomerNameAndBusinessKey = computed(() =>
    mapReportCustomerNameAndBusinessKey(this.customer(), {
      showBusinessKeyInFooter: this.environment.components.reportShowCustomerBusinessKeyInFooter === true
    })
  );
  protected readonly organizationId = computed(() => this.reportCustomerNameAndBusinessKey().businessKey);
  protected readonly organizationName = computed(() => this.reportCustomerNameAndBusinessKey().name);
  protected readonly labelDistribution = toSignal(
    combineLatest([
      toObservable(this.portfolioId),
      toObservable(this.buildingSearchFiltersService.searchFilters),
      toObservable(this.buildingSearchFiltersService.isLoadingSearchFilters)
    ]).pipe(
      switchMap(([portfolioId, searchFilters, isLoadingSearchFilters]) => {
        if (!portfolioId || isLoadingSearchFilters) {
          return of(undefined);
        }
        return this.portfoliosService.v2PortfoliosIdLabelDistributionPost(searchFilters, portfolioId);
      })
    )
  );
  protected readonly labelGroup = computed(() => {
    const labelDistribution = this.labelDistribution();
    if (!labelDistribution) {
      return undefined;
    }
    const queryLabelGroup = this.labelGroupService.queryParamLabelGroup();
    if (queryLabelGroup) {
      return queryLabelGroup;
    }
    const labelGroups = this.labelGroupService.getLabelGroups(labelDistribution.energyLabels);
    return this.labelGroupService.getDefaultLabelGroup(labelGroups);
  });
}
